// Git's object database, read-only: loose objects and packfiles (with their deltas), read
// through GitFiles, so it works on a browser folder handle, an uploaded copy or Node's fs.
import { unzlibSync } from 'fflate';

/** The files of a `.git` folder. Paths are relative to it, with `/` separators. */
export interface GitFiles {
  /** The bytes of a file, or of the range [start, end); null when it does not exist. */
  read(path: string, start?: number, end?: number): Promise<Uint8Array | null>;
  /** The size of a file in bytes; null when it does not exist. */
  size(path: string): Promise<number | null>;
  /** The names of the entries in a folder; empty when it does not exist. */
  list(path: string): Promise<string[]>;
}

export type ObjectType = 'commit' | 'tree' | 'blob' | 'tag';

export interface GitObject {
  type: ObjectType;
  data: Uint8Array;
}

export interface ObjectStoreOptions {
  /** Packs up to this size are read whole on first use; larger ones in chunks. */
  wholePackLimit?: number;
  /** The chunk size for reading larger packs. */
  chunkSize?: number;
}

const TYPES: Record<number, ObjectType> = { 1: 'commit', 2: 'tree', 3: 'blob', 4: 'tag' };
const OFS_DELTA = 6;
const REF_DELTA = 7;
/** Resolved objects kept for delta chains and repeated reads (bytes). */
const CACHE_BYTES = 48 * 1024 * 1024;
const CACHE_ONE_LIMIT = 4 * 1024 * 1024;

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

export function toHex(bytes: Uint8Array, start = 0, length = bytes.length - start): string {
  let s = '';
  for (let i = start; i < start + length; i++) s += HEX[bytes[i]!];
  return s;
}

export function fromHex(hex: string): Uint8Array {
  const out = new Uint8Array(hex.length >> 1);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

function inflate(data: Uint8Array, size: number): Uint8Array {
  if (size === 0) return new Uint8Array(0);
  return unzlibSync(data, { out: new Uint8Array(size) });
}

/** Builds an object from its delta base and a git delta (copy and insert instructions). */
export function applyDelta(base: Uint8Array, delta: Uint8Array): Uint8Array {
  let pos = 0;
  const varint = () => {
    let value = 0;
    let shift = 1;
    let c: number;
    do {
      c = delta[pos++]!;
      value += (c & 0x7f) * shift;
      shift *= 128;
    } while (c & 0x80);
    return value;
  };
  const baseSize = varint();
  if (baseSize !== base.length) throw new Error('Corrupt delta: the base has another size.');
  const out = new Uint8Array(varint());
  let at = 0;
  while (pos < delta.length) {
    const op = delta[pos++]!;
    if (op & 0x80) {
      let offset = 0;
      let size = 0;
      if (op & 0x01) offset = delta[pos++]!;
      if (op & 0x02) offset |= delta[pos++]! << 8;
      if (op & 0x04) offset |= delta[pos++]! << 16;
      if (op & 0x08) offset = (offset | (delta[pos++]! << 24)) >>> 0;
      if (op & 0x10) size = delta[pos++]!;
      if (op & 0x20) size |= delta[pos++]! << 8;
      if (op & 0x40) size |= delta[pos++]! << 16;
      if (size === 0) size = 0x10000;
      out.set(base.subarray(offset, offset + size), at);
      at += size;
    } else if (op) {
      out.set(delta.subarray(pos, pos + op), at);
      pos += op;
      at += op;
    } else {
      throw new Error('Corrupt delta: an empty instruction.');
    }
  }
  if (at !== out.length) throw new Error('Corrupt delta: the result has another size.');
  return out;
}

function compare(a: Uint8Array, aStart: number, b: Uint8Array, length: number): number {
  for (let i = 0; i < length; i++) {
    const d = a[aStart + i]! - b[i]!;
    if (d) return d;
  }
  return 0;
}

const u32 = (b: Uint8Array, at: number) => ((b[at]! << 24) | (b[at + 1]! << 16) | (b[at + 2]! << 8) | b[at + 3]!) >>> 0;

/** A pack's `.idx` file (version 1 or 2): the ids it holds, sorted, and where each starts. */
class PackIndex {
  readonly count: number;
  private readonly version: 1 | 2;
  private readonly fanout: number;

  constructor(
    private readonly bytes: Uint8Array,
    private readonly idLength: number,
  ) {
    this.version = u32(bytes, 0) === 0xff744f63 ? 2 : 1;
    if (this.version === 2 && u32(bytes, 4) !== 2) throw new Error(`Unsupported pack index version ${u32(bytes, 4)}.`);
    this.fanout = this.version === 2 ? 8 : 0;
    this.count = u32(bytes, this.fanout + 255 * 4);
  }

  private idAt(i: number): number {
    return this.version === 2 ? this.fanout + 1024 + i * this.idLength : 1024 + i * (4 + this.idLength) + 4;
  }

  offsetAt(i: number): number {
    if (this.version === 1) return u32(this.bytes, 1024 + i * (4 + this.idLength));
    const table = this.fanout + 1024 + this.count * (this.idLength + 4);
    const small = u32(this.bytes, table + i * 4);
    if (!(small & 0x80000000)) return small;
    const large = table + this.count * 4 + (small & 0x7fffffff) * 8;
    return u32(this.bytes, large) * 2 ** 32 + u32(this.bytes, large + 4);
  }

  /** The offset of an object in the pack, or -1. */
  find(id: Uint8Array): number {
    const first = id[0]!;
    let lo = first === 0 ? 0 : u32(this.bytes, this.fanout + (first - 1) * 4);
    let hi = u32(this.bytes, this.fanout + first * 4) - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const c = compare(this.bytes, this.idAt(mid), id, this.idLength);
      if (c === 0) return this.offsetAt(mid);
      if (c < 0) lo = mid + 1;
      else hi = mid - 1;
    }
    return -1;
  }
}

interface Resolved {
  type: number;
  data: Uint8Array;
}

/** A packfile and its index. Objects are read by offset; their ends come from the sorted offsets. */
class Pack {
  private index: PackIndex | null = null;
  private ends: Float64Array | null = null;
  private whole: Uint8Array | null = null;
  private size = 0;
  private readonly chunks = new Map<number, Uint8Array>();

  constructor(
    private readonly store: ObjectStore,
    readonly name: string,
  ) {}

  private get files(): GitFiles {
    return this.store.files;
  }

  async load(): Promise<void> {
    const idx = await this.files.read(`objects/pack/${this.name}.idx`);
    const size = await this.files.size(`objects/pack/${this.name}.pack`);
    if (!idx || size === null) throw new Error(`Pack ${this.name} is incomplete.`);
    this.index = new PackIndex(idx, this.store.idLength);
    this.size = size;
  }

  find(id: Uint8Array): number {
    return this.index ? this.index.find(id) : -1;
  }

  /** Where the object starting at `offset` ends: the next object, or the trailing checksum. */
  private end(offset: number): number {
    if (!this.ends) {
      const all = new Float64Array(this.index!.count);
      for (let i = 0; i < all.length; i++) all[i] = this.index!.offsetAt(i);
      this.ends = all.sort();
    }
    const ends = this.ends;
    let lo = 0;
    let hi = ends.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (ends[mid]! <= offset) lo = mid + 1;
      else hi = mid - 1;
    }
    return lo < ends.length ? ends[lo]! : this.size - this.store.idLength;
  }

  private async bytes(start: number, end: number): Promise<Uint8Array> {
    const path = `objects/pack/${this.name}.pack`;
    const { wholePackLimit, chunkSize } = this.store.options;
    if (this.size <= wholePackLimit) {
      this.whole ??= await this.files.read(path);
      if (!this.whole) throw new Error(`Pack ${this.name} could not be read.`);
      return this.whole.subarray(start, end);
    }
    if (end - start > chunkSize) return (await this.files.read(path, start, end)) ?? new Uint8Array(0);
    const first = Math.floor(start / chunkSize);
    const last = Math.floor((end - 1) / chunkSize);
    const parts: Uint8Array[] = [];
    for (let c = first; c <= last; c++) {
      let chunk = this.chunks.get(c);
      if (chunk) {
        this.chunks.delete(c);
      } else {
        chunk = (await this.files.read(path, c * chunkSize, Math.min(this.size, (c + 1) * chunkSize))) ?? new Uint8Array(0);
        if (this.chunks.size >= 48) this.chunks.delete(this.chunks.keys().next().value!);
      }
      this.chunks.set(c, chunk);
      parts.push(chunk);
    }
    if (parts.length === 1) return parts[0]!.subarray(start - first * chunkSize, end - first * chunkSize);
    const out = new Uint8Array(end - start);
    let at = 0;
    parts.forEach((chunk, i) => {
      const from = i === 0 ? start - first * chunkSize : 0;
      const to = i === parts.length - 1 ? end - (first + i) * chunkSize : chunk.length;
      out.set(chunk.subarray(from, to), at);
      at += to - from;
    });
    return out;
  }

  async object(offset: number): Promise<Resolved> {
    const key = `${this.name}:${offset}`;
    const cached = this.store.cached(key);
    if (cached) return cached;
    const bytes = await this.bytes(offset, this.end(offset));
    let pos = 0;
    let c = bytes[pos++]!;
    const type = (c >> 4) & 7;
    let size = c & 15;
    let shift = 16;
    while (c & 0x80) {
      c = bytes[pos++]!;
      size += (c & 0x7f) * shift;
      shift *= 128;
    }
    let result: Resolved;
    if (type === OFS_DELTA) {
      c = bytes[pos++]!;
      let back = c & 0x7f;
      while (c & 0x80) {
        c = bytes[pos++]!;
        back = (back + 1) * 128 + (c & 0x7f);
      }
      const base = await this.object(offset - back);
      result = { type: base.type, data: applyDelta(base.data, inflate(bytes.subarray(pos), size)) };
    } else if (type === REF_DELTA) {
      const baseId = toHex(bytes, pos, this.store.idLength);
      pos += this.store.idLength;
      const base = await this.store.raw(baseId);
      if (!base) throw new Error(`Missing delta base ${baseId}.`);
      result = { type: base.type, data: applyDelta(base.data, inflate(bytes.subarray(pos), size)) };
    } else if (TYPES[type]) {
      result = { type, data: inflate(bytes.subarray(pos), size) };
    } else {
      throw new Error(`Unknown object type ${type} in pack ${this.name}.`);
    }
    this.store.remember(key, result);
    return result;
  }
}

/** Reads objects by id from the packs and the loose objects of a `.git` folder. */
export class ObjectStore {
  readonly options: Required<ObjectStoreOptions>;
  private packs: Pack[] = [];
  private readonly cache = new Map<string, Resolved>();
  private cacheBytes = 0;
  private refreshed = 0;

  constructor(
    readonly files: GitFiles,
    /** 20 for SHA-1 repositories, 32 for SHA-256. */
    readonly idLength: number,
    options: ObjectStoreOptions = {},
  ) {
    this.options = { wholePackLimit: options.wholePackLimit ?? 64 * 1024 * 1024, chunkSize: options.chunkSize ?? 1024 * 1024 };
  }

  /** Lists the packs again, keeping those already open (git adds packs, and repacking replaces them). */
  async refresh(): Promise<void> {
    this.refreshed = Date.now();
    const names = (await this.files.list('objects/pack')).filter((n) => n.endsWith('.idx')).map((n) => n.slice(0, -4));
    const open = new Map(this.packs.map((p) => [p.name, p]));
    const packs: Pack[] = [];
    for (const name of names) {
      let pack = open.get(name);
      if (!pack) {
        pack = new Pack(this, name);
        try {
          await pack.load();
        } catch {
          // A pack being written right now (its .pack not complete yet); the next refresh picks it up.
          continue;
        }
      }
      packs.push(pack);
    }
    this.packs = packs;
  }

  cached(key: string): Resolved | undefined {
    const hit = this.cache.get(key);
    if (hit) {
      this.cache.delete(key);
      this.cache.set(key, hit);
    }
    return hit;
  }

  remember(key: string, value: Resolved): void {
    if (value.data.length > CACHE_ONE_LIMIT || this.cache.has(key)) return;
    this.cache.set(key, value);
    this.cacheBytes += value.data.length;
    while (this.cacheBytes > CACHE_BYTES) {
      const [oldest, entry] = this.cache.entries().next().value!;
      this.cache.delete(oldest);
      this.cacheBytes -= entry.data.length;
    }
  }

  async raw(id: string): Promise<Resolved | null> {
    const found = await this.find(id);
    if (found) return found;
    // Objects packed since the packs were listed (git gc, a fetch). Not on every miss: a shallow or
    // partial clone misses objects all the time.
    if (Date.now() - this.refreshed < 2000) return null;
    this.refreshed = Date.now();
    await this.refresh();
    return this.find(id);
  }

  private async find(id: string): Promise<Resolved | null> {
    const bin = fromHex(id);
    for (const pack of this.packs) {
      const offset = pack.find(bin);
      if (offset >= 0) return pack.object(offset);
    }
    const cached = this.cached(id);
    if (cached) return cached;
    const file = await this.files.read(`objects/${id.slice(0, 2)}/${id.slice(2)}`);
    if (!file) return null;
    const full = unzlibSync(file);
    const space = full.indexOf(0x20);
    const nul = full.indexOf(0, space);
    const name = new TextDecoder().decode(full.subarray(0, space));
    const type = Number(Object.entries(TYPES).find(([, t]) => t === name)?.[0] ?? 0);
    if (!type) throw new Error(`Unknown object type "${name}" in ${id}.`);
    const result = { type, data: full.subarray(nul + 1) };
    this.remember(id, result);
    return result;
  }

  /** An object by id, or null when the repository does not have it (a shallow or partial clone). */
  async read(id: string): Promise<GitObject | null> {
    const raw = await this.raw(id);
    return raw ? { type: TYPES[raw.type]!, data: raw.data } : null;
  }
}
